import { API_PREFIX, ErrorBody, ErrorCode } from '@kcm/contracts'
import type { z } from 'zod'

/**
 * The one way this app talks to its API (PLAN.md §14).
 *
 * Three things it guarantees so no screen has to remember them:
 *
 *   - **Same-origin, always.** The bundle is served from the site's own domain
 *     and the session cookie is host-only (§4.4), so a cross-origin request
 *     would silently drop the cookie and look like a logged-out user.
 *   - **Every error is an `ApiError` with a `code`.** Screens branch on the
 *     code, never on the message — the message is display copy and may be
 *     localised out from under them.
 *   - **Responses are parsed, not cast.** A contract drift fails here, loudly,
 *     instead of surfacing as an undefined three components deep.
 */

/** Anything the server rejected, plus everything that never reached it. */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    /** Per-field messages, ready to hang off the matching input. */
    readonly fields?: Record<string, string[]>,
    /** Ties a support conversation to the server log line. */
    readonly correlationId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when retrying the identical request could plausibly work. */
  get retryable(): boolean {
    return this.status >= 500 || this.status === 0 || this.code === ErrorCode.RATE_LIMITED
  }
}

/**
 * A failure before the server had a say — offline, DNS, a tunnel that died.
 *
 * Given as status 0 rather than a thrown TypeError so callers have exactly one
 * error type to handle. §11.1 matters here: a meaningful share of this funnel
 * runs inside in-app browsers that drop connections when the app backgrounds.
 */
const OFFLINE = 'network_unavailable'

interface RequestOptions {
  signal?: AbortSignal
  /** Sent as JSON. Omit for GET. */
  body?: unknown
}

// Generic over the schema rather than its output type: inferring `T` from
// `z.ZodType<T>` collapses to `{}` for object schemas, which then loses every
// field at the call site.
async function request<S extends z.ZodTypeAny>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  schema: S,
  options: RequestOptions = {},
): Promise<z.infer<S>> {
  let res: Response
  try {
    res = await fetch(`${API_PREFIX}${path}`, {
      method,
      credentials: 'same-origin',
      headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    })
  } catch (err) {
    // An aborted request is the caller changing its mind, not a failure; let it
    // propagate so React Query treats it as a cancellation.
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError(OFFLINE, 'Check your connection and try again.', 0)
  }

  if (!res.ok) throw await toApiError(res)

  const json: unknown = await res.json().catch(() => undefined)
  const parsed = schema.safeParse(json)
  if (!parsed.success) {
    // Deliberately not surfacing the zod issue: it names our own internals and
    // means nothing to the person reading it.
    throw new ApiError('malformed_response', 'Something went wrong. Please try again.', res.status)
  }
  return parsed.data
}

/**
 * Turns a non-2xx into an `ApiError`.
 *
 * The server always sends `ErrorBody`, but "always" has to survive a proxy
 * returning its own HTML 502 — hence the fallback rather than a parse that
 * throws and buries the real status.
 */
async function toApiError(res: Response): Promise<ApiError> {
  const json: unknown = await res.json().catch(() => undefined)
  const body = ErrorBody.safeParse(json)
  if (!body.success) {
    return new ApiError(
      res.status >= 500 ? ErrorCode.INTERNAL : ErrorCode.BAD_REQUEST,
      genericFor(res.status),
      res.status,
    )
  }
  return new ApiError(
    body.data.code,
    body.data.message,
    res.status,
    body.data.fields,
    body.data.correlationId,
  )
}

function genericFor(status: number): string {
  if (status === 429) return 'Too many attempts. Please wait a moment and try again.'
  if (status >= 500) return 'Something went wrong on our side. Please try again.'
  return 'That request could not be completed.'
}

export const api = {
  get: <S extends z.ZodTypeAny>(path: string, schema: S, signal?: AbortSignal) =>
    request('GET', path, schema, signal ? { signal } : {}),
  post: <S extends z.ZodTypeAny>(path: string, schema: S, body?: unknown, signal?: AbortSignal) =>
    request('POST', path, schema, { body: body ?? {}, ...(signal ? { signal } : {}) }),
  del: <S extends z.ZodTypeAny>(path: string, schema: S, signal?: AbortSignal) =>
    request('DELETE', path, schema, signal ? { signal } : {}),
}
